import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import styled from 'styled-components'

import { BillingInfo } from 'account/models/BillingInfo'
import RadioButton from 'checkout/components/RadioButton'
import RadioGroup from 'checkout/components/RadioGroup'
import { useCheckoutContext } from 'checkout/containers/CheckoutContextProvider'
import PaymentOption from 'checkout/enums/PaymentOption'
import { paymentOptionDisplayMap } from 'checkout/utils/paymentOptionDisplayMap'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { t } from 'localization'
import AffirmPromotional, {
  AffirmPromotionalPageType,
  AffirmPromotionalType,
} from 'productTemplate/components/AffirmPromotional'
import {
  AfterPayPromotional,
  AfterPayPromotionalType,
} from 'productTemplate/components/AfterPayPromotional'
import { useMediaQuery } from 'react-responsive'
import { Button } from 'shared/components/Button'
import { AffirmIcon, AfterPayIcon, CardIcon, KlarnaIcon, PayPalIcon } from 'shared/components/Icons'
import { CardBrand } from 'shared/components/Icons/PaymentIcons/CardIcon/CardIcon'
import { FCLink } from 'shared/components/Links'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { useGetPaymentAvailabilityQuery } from 'api/paymentAvailabilityApi'
import Spinner from 'shared/components/Spinner'
import { CheckoutQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import PaymentType from 'shared/enums/PaymentType'
import { colors, fonts } from 'shared/lib'
import { sizes } from 'shared/lib/media'
import { KlarnaPromotional } from './KlarnaPromotional'

const TP = 'checkout.components.PaymentMethodPanel.savedPaymentMehodsForm'

const ContinueButton = styled(Button)`
  margin-top: 5px;
`

const getDefaultPaymentMethod = (billingInfo?: BillingInfo) => {
  switch (billingInfo?.paymentType) {
    case PaymentType.PayPal:
      return PaymentOption.PayPal
    case PaymentType.PayPalv2:
      return PaymentOption.PayPalv2
    case PaymentType.AfterPay:
      return PaymentOption.AfterPay
    case PaymentType.Affirm:
      return PaymentOption.Affirm
    case PaymentType.Klarna:
      return PaymentOption.Klarna
    case PaymentType.CreditCard:
      return billingInfo?.id || PaymentOption.NewCard
    default:
      return PaymentOption.NewCard
  }
}

const SavedPaymentMethodsForm = () => {
  const {
    panels,
    creditCards,
    getCreditCards,
    initBraintree,
    order,
    selectAffirm,
    selectAfterPay,
    selectKlarna,
    selectCreditCard,
    selectPayPal,
    selectPayPalv2,
    updatePanel,
  } = useCheckoutContext()

  const tv = useTranslation()
  const defaultPaymentMethod = getDefaultPaymentMethod(order?.billingInfo)
  const [selectedMethod, setSelectedMethod] = useState(defaultPaymentMethod)
  const { country: selectedCountryCode, currencyCode: selectedCurrency } =
    useShoppingRegionContext()
  const { loading, view, isEditing } = panels.paymentMethod
  const orderRequiresPayment = !!order?.finalPrice?.value
  const klarnaPriceEligible =
    order && order?.finalPrice?.value > 3500 && order?.finalPrice?.value < 1000000
  const isPayPalEnabled = useFeatureFlag(FeatureFlag.WEB_FC_ENABLE_PAYPAL)
  const isKlarnaEnabled = useFeatureFlag(FeatureFlag.WEB_FC_ENABLE_KLARNA)
  const isPayPalv2Enabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_PAYPAL_TWO)
  const isAfterPayExpressEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_EXPRESS_AFTERPAY)
  const isExpressAffirmEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_EXPRESS_AFFIRM)
  const isPaymentAvailabilityServiceEnabled = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_ENABLE_PAYMENT_AVAILABILITY_SERVICE,
  )
  const isUsd = selectedCurrency === 'USD'

  const { data: paymentAvailability } = useGetPaymentAvailabilityQuery(
    {
      country_code: selectedCountryCode,
      currency: selectedCurrency,
      final_price_cents: order?.finalPrice?.value as number,
      // orderTypes: 1 is regular purchase, 2 is offer, 3 is drop
      order_type: 1,
      goat_storage: false,
      shipping_region: order?.shippingAddress?.countryCode,
    },
    {
      // When skip is true, it doesnt make server call.
      skip: !isPaymentAvailabilityServiceEnabled,
    },
  )

  const willRedirect =
    selectedMethod === PaymentOption.Affirm ||
    selectedMethod === PaymentOption.PayPal ||
    selectedMethod === PaymentOption.PayPalv2 ||
    selectedMethod === PaymentOption.Klarna ||
    selectedMethod === PaymentOption.AfterPay

  const showKlarna = isPaymentAvailabilityServiceEnabled
    ? paymentAvailability?.klarna
    : isKlarnaEnabled && klarnaPriceEligible && orderRequiresPayment && isUsd
  const showAfterPay = isPaymentAvailabilityServiceEnabled
    ? paymentAvailability?.afterpay
    : order?.isAfterpayEligible && isUsd
  const showAffirm = isPaymentAvailabilityServiceEnabled
    ? paymentAvailability?.affirm
    : isExpressAffirmEnabled && isUsd
  const showPayPal = isPaymentAvailabilityServiceEnabled
    ? paymentAvailability?.paypal
    : isPayPalEnabled && orderRequiresPayment && isUsd

  useEffect(() => {
    if (isEditing && view === 'saved') {
      getCreditCards()
    }
  }, [view, isEditing])

  useEffect(() => {
    initBraintree()
  }, [initBraintree])

  const handleContinueClick = async () => {
    if (typeof selectedMethod === 'number') {
      selectCreditCard(selectedMethod)
    }
    if (selectedMethod === PaymentOption.NewCard) {
      updatePanel('paymentMethod', { view: 'new' })
    }
    if (selectedMethod === PaymentOption.Affirm) {
      selectAffirm()
    }
    if (selectedMethod === PaymentOption.PayPal) {
      selectPayPal()
    }
    if (selectedMethod === PaymentOption.PayPalv2) {
      selectPayPalv2()
    }
    if (selectedMethod === PaymentOption.AfterPay) {
      selectAfterPay()
    }
    if (selectedMethod === PaymentOption.Klarna) {
      selectKlarna()
    }
  }

  const isMobile = useMediaQuery({ maxWidth: sizes.small })

  const continueButtonText = (() => {
    switch (true) {
      case selectedMethod === PaymentOption.Klarna:
        return t(`${TP}.placeOrderWithKlarna`, 'Place order with Klarna')
      case selectedMethod === PaymentOption.PayPalv2:
        return t(`${TP}.placeOrderWithPayPal`, 'Place Order with PayPal')
      case selectedMethod === PaymentOption.Affirm && isExpressAffirmEnabled:
        return t(`${TP}.placeOrderWithAffirm`, 'Place Order with Affirm')
      case selectedMethod === PaymentOption.AfterPay && isAfterPayExpressEnabled:
        return t(`${TP}.placeOrderWithAfterPay`, 'Place Order with Afterpay')
      default:
        return t(`${TP}.continue`, 'Continue')
    }
  })()

  const continueButtonSubHeader = (() => {
    switch (true) {
      case selectedMethod === PaymentOption.Klarna:
        return t(
          `${TP}.redirectedToKlarna`,
          'You will be redirected to Klarna to complete your purchase securely.',
        )
      case selectedMethod === PaymentOption.PayPalv2:
        return t(
          `${TP}.redirectedToPayPal`,
          'You will be redirected to PayPal to complete your purchase securely.',
        )
      case selectedMethod === PaymentOption.Affirm && isExpressAffirmEnabled:
        return t(
          `${TP}.redirectedToAffirm`,
          'You will be redirected to Affirm to provide your payment information.',
        )
      case selectedMethod === PaymentOption.AfterPay && isAfterPayExpressEnabled:
        return t(
          `${TP}.redirectedToAfterPay`,
          'You will be redirected to Afterpay to provide your payment information.',
        )
      default:
        return t(`${TP}.redirectedToOthers`, {
          defaultValue:
            'You will be redirected to {paymentOption} to provide your payment information. To complete your order you must return to Flight Club.',
          paymentOption: paymentOptionDisplayMap.get(selectedMethod as PaymentOption),
        })
    }
  })()

  const shouldShowTermsAndPrivacyText =
    selectedMethod === PaymentOption.Klarna ||
    selectedMethod === PaymentOption.PayPalv2 ||
    selectedMethod === PaymentOption.Affirm ||
    selectedMethod === PaymentOption.AfterPay

  return (
    <Spinner
      showSpinner={loading}
      text={t(`${TP}.spinnerText`, 'Updating payment info...')}
      subText={t(`${TP}.spinnerSubText`, 'Please do not close your browser!')}
    >
      <RadioGroup
        data-testid="payment-methods"
        qaAttr={CheckoutQA.PaymentMethodRadioGroup}
        name="paymentMethods"
      >
        {!!creditCards?.length &&
          creditCards.map(({ id, cardBrand, last4Digits }) => (
            <PaymentMethodRadio
              qaAttr={CheckoutQA.CreditCardOption}
              title={
                <>
                  <CardIcon brand={cardBrand} />
                  <SavedCreditCardSpan>
                    {` ${cardBrand?.toUpperCase()} *${last4Digits}`}
                  </SavedCreditCardSpan>
                </>
              }
              key={id}
              value={id.toString()}
              checked={id === selectedMethod}
              onRadioSelect={() => setSelectedMethod(id)}
              data-testid={`creditCardButton${id}`}
            />
          ))}
        <PaymentMethodRadio
          isMobile={isMobile}
          qaAttr={CheckoutQA.AddNewCardOption}
          title={t(`${TP}.addNewCard`, 'Add New Card')}
          value="newCard"
          checked={PaymentOption.NewCard === selectedMethod}
          onRadioSelect={() => setSelectedMethod(PaymentOption.NewCard)}
          tags={
            <div>
              <StyledCardIcon brand={CardBrand.Visa} />
              <StyledCardIcon brand={CardBrand.MasterCard} />
              <StyledCardIcon brand={CardBrand.AmericanExpress} />
            </div>
          }
        />
        {(showKlarna || showAffirm || showPayPal || showAfterPay) && isUsd && <Divider />}
        {showKlarna && (
          <PaymentMethodRadio
            isMobile={isMobile}
            data-testid="klarnaOptionButton"
            qaAttr={CheckoutQA.KlarnaOption}
            title={<KlarnaIcon />}
            tags={
              <StyledPaymentSubTitle isMobile={isMobile}>
                <KlarnaPromotional price={order?.finalPrice.value} />
              </StyledPaymentSubTitle>
            }
            value="klarnaButton"
            checked={PaymentOption.Klarna === selectedMethod}
            onRadioSelect={() => setSelectedMethod(PaymentOption.Klarna)}
          />
        )}
        {showAffirm && (
          <PaymentMethodRadio
            isMobile={isMobile}
            data-testid="affirmOptionButton"
            qaAttr={CheckoutQA.AffirmOption}
            title={<StyledAffirmIcon />}
            value="affirmButton"
            tags={
              <StyledPaymentSubTitle isMobile={isMobile}>
                <StyledAffirmPromotional
                  amount={order?.finalPrice.value}
                  affirmType={AffirmPromotionalType.Text}
                  pageType={AffirmPromotionalPageType.Payment}
                />
              </StyledPaymentSubTitle>
            }
            checked={PaymentOption.Affirm === selectedMethod}
            onRadioSelect={() => setSelectedMethod(PaymentOption.Affirm)}
          />
        )}
        {showPayPal && !isPayPalv2Enabled && (
          <PaymentMethodRadio
            isMobile={isMobile}
            data-testid="paypalOptionButton"
            qaAttr={CheckoutQA.PayPalOption}
            title={<PayPalIcon />}
            value="paypalButton"
            checked={PaymentOption.PayPal === selectedMethod}
            onRadioSelect={() => setSelectedMethod(PaymentOption.PayPal)}
          />
        )}
        {showPayPal && isPayPalv2Enabled && (
          <PaymentMethodRadio
            isMobile={isMobile}
            data-testid="paypalOptionButton"
            qaAttr={CheckoutQA.PayPalv2Option}
            title={<PayPalIcon />}
            value="paypalv2Button"
            checked={PaymentOption.PayPalv2 === selectedMethod}
            onRadioSelect={() => setSelectedMethod(PaymentOption.PayPalv2)}
          />
        )}
        {showAfterPay && (
          <PaymentMethodRadio
            isMobile={isMobile}
            qaAttr={CheckoutQA.AfterPayOption}
            title={<AfterPayIcon />}
            value="afterPay"
            checked={PaymentOption.AfterPay === selectedMethod}
            onRadioSelect={() => setSelectedMethod(PaymentOption.AfterPay)}
            tags={
              <StyledPaymentSubTitle isMobile={isMobile}>
                <AfterPayPromotional type={AfterPayPromotionalType.Text} />
              </StyledPaymentSubTitle>
            }
            data-testid="afterPayButton"
          />
        )}
      </RadioGroup>
      <ContinueButton
        $fill
        qaAttr={CheckoutQA.PaymentMethodContinueButton}
        dataTestid="continueButton"
        buttonType="primary1"
        onClick={handleContinueClick}
      >
        {continueButtonText}
      </ContinueButton>
      {willRedirect && (
        <RedirectNotice
          data-qa={CheckoutQA.PaymentRedirectNotice}
          data-testid="paymentRedirectNotice"
        >
          {continueButtonSubHeader}
          {shouldShowTermsAndPrivacyText && (
            <TermsAndPrivacyText>
              <Trans
                i18nKey={`${TP}.TermsAndPrivacyText`}
                t={tv.t}
                defaults="By proceeding, you acknowledge that you have read and agree to the <0>Terms & Conditions</0> and the <1>Privacy Policy</1>."
                components={[
                  <FCLink newTab href="/terms-conditions" />,
                  <FCLink newTab href="/privacy" />,
                ]}
              />
            </TermsAndPrivacyText>
          )}
        </RedirectNotice>
      )}
    </Spinner>
  )
}

export default SavedPaymentMethodsForm

const Divider = styled.div`
  border-bottom: 1px solid ${colors.FC2_LIGHTEST_GREY};
  margin: 24px 0;
`

const StyledCardIcon = styled(CardIcon)`
  margin: 0 5px;
`

const StyledAffirmPromotional = styled(AffirmPromotional)`
  ${fonts.BODY_TEXT}
`

const StyledAffirmIcon = styled(AffirmIcon)`
  flex-shrink: 0;
`

const RedirectNotice = styled.p`
  margin-top: 20px;
  ${fonts.BODY_TEXT}
`

const PaymentMethodRadio = styled(RadioButton)`
  height: 64px;
  padding: ${(props) => (props.isMobile ? '15px 20px' : '20px')};
`

const SavedCreditCardSpan = styled.span`
  vertical-align: super;
`

const StyledPaymentSubTitle = styled.div<{ isMobile: boolean }>`
  width: ${(props) => (props.isMobile ? '60%' : 'auto')};
`

const TermsAndPrivacyText = styled.div`
  padding-top: 20px;
`
